import useTrans from "@/hooks/useTrans"
import CloseIcon from "@mui/icons-material/Close"
import {
  Button,
  IconButton,
  Dialog,
  Stack,
  useTheme,
  useMediaQuery,
  Drawer,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material"
import { FC, forwardRef, ReactNode, useMemo } from "react"
import { TransitionProps } from "@mui/material/transitions"
import Slide from "@mui/material/Slide"
import cls from "classnames"
import * as dialogStyles from "../relendDialog/styles"

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction='up' ref={ref} {...props} />
})

type RelendModalProps = Record<`data-${string}`, any> & {
  open: boolean
  handleClose: () => void
  children: ReactNode
  title?: string
  hideButtonClose?: boolean
  showCloseButton?: boolean
  isSmallPopup?: boolean
  useDrawerOnMobile?: boolean
  className?: string
  textButtonClose?: string
  zIndex?: number
}

const View: FC<RelendModalProps> = ({
  open,
  handleClose,
  children,
  title,
  hideButtonClose,
  showCloseButton = false,
  isSmallPopup = false,
  useDrawerOnMobile = false,
  className,
  textButtonClose,
  zIndex = 1300,
  ...props
}) => {
  const trans = useTrans()
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up("sm"))

  const showTitle = useMemo(() => title ?? showCloseButton, [showCloseButton, title])
  const Component = useMemo(() => (useDrawerOnMobile && !isDesktop ? Drawer : Dialog), [isDesktop, useDrawerOnMobile])

  return (
    <Component
      {...props}
      open={open}
      onClose={handleClose}
      TransitionComponent={Transition}
      keepMounted
      aria-labelledby='modal-modal-title'
      aria-describedby='modal-modal-description'
      className={`!z-[${zIndex}]`}
      style={{ zIndex: zIndex }}
      PaperProps={{
        className: cls(
          className,
          Component === Dialog && "rounded-b-[16px]",
          isDesktop ? (isSmallPopup ? "!w-[375px]" : "!w-[500px]") : "min-w-full-width-dialog",
          "rounded-t-[16px]",
        ),
      }}
      scroll='paper'
      anchor='bottom'
    >
      {showTitle && (
        <DialogTitle sx={dialogStyles.dialogTitle}>
          {title}
          {showCloseButton && (
            <IconButton aria-label='close' className='sm:top-3' onClick={handleClose} sx={dialogStyles.dialogIconClose}>
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
      )}

      <DialogContent className={cls(Component === Dialog && "w-full py-4 px-6", "bg-white pb-0")}>
        <Stack className={showTitle ? "mt-4" : ""}>{children}</Stack>
      </DialogContent>

      <DialogActions>
        <Stack alignItems='center' className='w-full'>
          {!hideButtonClose && (
            <Button
              data-testid='modal-close-button'
              onClick={handleClose}
              className='font-semibold text-primary hover:text-primary/80 underline hover:underline hover:bg-transparent py-1 rounded'
            >
              {textButtonClose ?? trans.dashboard.modal.close}
            </Button>
          )}
        </Stack>
      </DialogActions>
    </Component>
  )
}

export default View
